// Helper functions for export operations

import { exportToExcel, exportMultipleToExcel } from '../utils/exportToExcel';
import { roomsAPI } from '../services/api';

/**
 * Export hotels to Excel
 * @param {Array} hotels - Array of hotel objects
 * @returns {void}
 */
export const exportHotelsToExcel = (hotels) => {
  const exportData = hotels.map(hotel => ({
    ID: hotel.id || '',
    'Hotel Name (EN)': hotel.name_en || '',
    'Accessibility Template IDs': hotel.accessibility_template_ids ? hotel.accessibility_template_ids.join(', ') : '',
    City: hotel.city || '',
    Country: hotel.country || '',
    'Supplier ID': hotel.supplier_id || '',
    'Commission Percentage': hotel.commission_percentage || '',
    Featured: hotel.featured ? 'Yes' : 'No',
    'Image URLs': hotel.images_urls ? hotel.images_urls.join('; ') : ''
  }));
  
  exportToExcel(exportData, 'hotels', 'Hotels');
};

/**
 * Export rooms to Excel for a specific hotel
 * @param {string} hotelId - The hotel ID
 * @param {string} hotelName - The hotel name
 * @param {Function} setErrors - State setter for errors
 * @returns {Promise<void>}
 */
export const exportRoomsToExcel = async (hotelId, hotelName, setErrors) => {
  try {
    // Fetch rooms for this hotel
    const response = await roomsAPI.getByHotelId(hotelId);
    const hotelRooms = response.data?.data?.rooms || [];
    
    const exportData = hotelRooms.map(room => ({
      'Room ID': room.id || '',
      'Hotel ID': room.hotel_id || '',
      'Room Type': room.room_type || '',
      'Room Name (EN)': room.name_en || '',
      'Description (EN)': room.description_en || '',
      'Price per Night': room.price_per_night || '',
      'Currency': room.currency || '',
      'Capacity': room.capacity || '',
      'Image URL': room.image_url || '',
      'Supplier ID': room.supplier_id || '',
      'Accessibility Template IDs': room.accessibility_template_ids ? room.accessibility_template_ids.join(', ') : ''
    }));
    
    exportToExcel(exportData, `rooms_${hotelName.replace(/\s+/g, '_')}`, `Rooms for ${hotelName}`);
  } catch (error) {
    console.error('Error exporting rooms:', error);
    setErrors(prev => ({ ...prev, exportRooms: error.message || 'Failed to export rooms' }));
  }
};

/**
 * Export all data (hotels and rooms) to Excel
 * @param {Array} hotels - Array of hotel objects
 * @param {Function} setErrors - State setter for errors
 * @param {Object} roomsAPI - The rooms API object
 * @returns {Promise<void>}
 */
export const exportAllToExcel = async (hotels, setErrors, roomsAPI) => {
  try {
    const hotelData = hotels.map(hotel => ({
      ID: hotel.id || '',
      'Hotel Name (EN)': hotel.name_en || '',
      'Accessibility Template IDs': hotel.accessibility_template_ids ? hotel.accessibility_template_ids.join(', ') : '',
      City: hotel.city || '',
      Country: hotel.country || '',
      'Supplier ID': hotel.supplier_id || '',
      'Commission Percentage': hotel.commission_percentage || '',
      Featured: hotel.featured ? 'Yes' : 'No',
      'Image URLs': hotel.images_urls ? hotel.images_urls.join('; ') : ''
    }));
    
    // Fetch all rooms
    const roomsResponse = await roomsAPI.getAll();
    const allRooms = roomsResponse.data?.data?.rooms || [];
    
    const roomData = allRooms.map(room => ({
      'Room ID': room.id || '',
      'Hotel ID': room.hotel_id || '',
      'Room Type': room.room_type || '',
      'Room Name (EN)': room.name_en || '',
      'Description (EN)': room.description_en || '',
      'Price per Night': room.price_per_night || '',
      'Currency': room.currency || '',
      'Capacity': room.capacity || '',
      'Image URL': room.image_url || '',
      'Supplier ID': room.supplier_id || '',
      'Accessibility Template IDs': room.accessibility_template_ids ? room.accessibility_template_ids.join(', ') : ''
    }));
    
    exportMultipleToExcel([
      { data: hotelData, sheetName: 'Hotels' },
      { data: roomData, sheetName: 'Rooms' }
    ], 'hotels_and_rooms');
  } catch (error) {
    console.error('Error exporting all data:', error);
    setErrors(prev => ({ ...prev, exportAll: error.message || 'Failed to export data' }));
  }
};

export default {
  exportHotelsToExcel,
  exportRoomsToExcel,
  exportAllToExcel
};